/* eslint-disable no-param-reassign */
import { Box, Button, CircularProgress, Grid, Icon } from '@material-ui/core';
import { NumericMetricInfo, TagMetricEnum, TagMetricInfo, TimeSeriesAggregation, TimeSeriesCollectionData, TimeSeriesCollectionRawData,
    TimeSeriesResolution } from '@moderepo/mode-apis';
import { DEFAULT_TSDB_SMART_MODULE_ID, getMidnightDate, convertCollectionDataToMetricsDataPoints,
    GlobalTimeSeriesInfo, useMergeCachedMetricDataToMultiMetricDataPoints, capitalizeAll, TimeSeriesType, Time,
    MultiSeriesDataPoint, sortMetrics, resolutionToMilliseconds, DataPoint, GenericMessageBox } from '@moderepo/mode-app-components';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import produce, { Draft } from 'immer';
import { fetchTimeSeriesCollectionInfo, fetchTimeSeriesCollectionRange, fetchTimeSeriesCollectionRawData, userAppDataContext,
    selectTimeSeriesCollectionInfo, selectTimeSeriesCollectionRange, selectTimeSeriesCollectionRawData, fetchTimeSeriesCollectionData,
    selectTimeSeriesCollectionData } from '@moderepo/mode-data-state-user-app';
import { fetchProjectDeviceById, ritzDataContext, selectProjectById, selectProjectDeviceById } from '@moderepo/mode-data-state-ritz';
import { modeUIContext, showSlideOutPanel, SlideOutPanelLocation } from '@moderepo/mode-ui-state';
import { useRitzTranslation } from '../../utils/customHooks';
import { MetricsDataDisplayContainer } from './MetricsDataDisplayContainer';
import { CachedMetricData, ChartSize, DUMMY_VALUE_NAME, ExtTimeSeriesAggregation, MAX_DATA_FIELD_POSTFIX, MIN_DATA_FIELD_POSTFIX }
    from './tsUtils';
import { DownloadTSDataCompOptions, SlideOutPanelCompType } from '../routes/defaultProjectConfig';



/**
 * An interface for keeping track of succeeded fetch data API calls
 */
interface FetchDataResult {
    readonly aggregation: TimeSeriesAggregation | undefined;
}



/**
 * We need this in a few places therefore will create an array of avg/min/max aggregation and reuse it.
 */
const AVG_MIN_MAX_AGGREGATIONS = [
    TimeSeriesAggregation.AVG,
    TimeSeriesAggregation.MIN,
    TimeSeriesAggregation.MAX,
];


export interface CollectionMetricsDataProviderProps {
    readonly projectId: number;

    // The container that uses this component must provide either deviceId or homeId. We actually need homeId but if homeId is not provided,
    // we can use deviceId to fetch device data and get the homeId from the device data.
    readonly deviceId?: number;
    readonly homeId?: number;

    readonly collectionId: string;
    readonly collectionDisplayName?: string | undefined;

    // Because this is a GENERIC collection historical data view, it can only show data provided by the collection APIs.
    // This mean valueNames and tagNames will be shown based on the valueNames and tagNames from collection info. Also,
    // these values won't have units because collection data doesn't have "unit" metadata.
    // So, the container of this component, can provide the numericMetrics and tagMetrics so that this component can use
    // these metric info to display more user friendly names for the data and even units.
    readonly numericMetrics?: readonly NumericMetricInfo[] | undefined;
    readonly tagMetrics?: readonly TagMetricInfo[] | undefined;

    readonly tsdbSmartModuleId?: string;

    readonly viewTypeSettings?: {
        chart?: {
            readonly excludedMetricsNames?: readonly string[];
        },
        tabular?: {
            readonly excludedMetricsNames?: readonly string[];
        }
    };
    readonly viewOptions?: {
        readonly combineSeries?: boolean | undefined;                    // whether or not to combine multiple series into 1 chart
        readonly chartSize?: ChartSize | undefined;
        readonly showChartView?: boolean | undefined;
        readonly showTabularView?: boolean | undefined;
        readonly showChartBullets?: boolean | undefined;
        readonly aggregation?: ExtTimeSeriesAggregation | undefined;
    };

    /**
     * This Container has an action that it needs to be displayed as a page CTA. In other to do this, the parent container must
     * implement the handler function setPageMainAction and take the provided 'action' and display it on the page. The parent container ODES NOT
     * need to do anything with this action other than displaying it. This container will take care of the UI of the action and will handle all the
     * events.
     */
    readonly setPageMainAction: (action: JSX.Element)=> void;
}


/**
 * This is a generic container used for displaying historical metric data for Collection data.
 */
export const CollectionMetricsDataProvider = (props: CollectionMetricsDataProviderProps) => {
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:appDataState, dispatch:appDataDispatch } = useContext(userAppDataContext);
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, props.projectId);
    const device = props.deviceId ? selectProjectDeviceById(dataState, props.deviceId) : undefined;
    const { trans } = useRitzTranslation(project);

    // Get timRange API call fail sometime so if this happen, we need to set this flag to true so that we use project's creationTime instead
    // value undefined means we are still waiting for the result.
    const [timeRangeCallFailed, setTimeRangeCallFailed] = useState<boolean | undefined>();

    // We need to make multiple API call to fetch data therefore we need to wait for all the data to be fetched before we can try to select
    // them from the data state. Therefore, this state is to keep track of when the data is loaded and which time series was fetched successfully.
    const [succeedFetchDataResults, setSucceedFetchDataResults] = useState<readonly FetchDataResult[] | undefined>();


    // We need homeId to fetch collection data. The homeId can be either in the route params if the user is in the route path that has
    // homeId. If not, we need to get the homeId from the device
    const collectionHomeId = useMemo(() => {
        if (props.homeId) {
            return props.homeId;
        }
        if (device && device.homeId) {
            return device.homeId;
        }
        return undefined;
    }, [device, props.homeId]);


    // This state is used for remembering the params we used for fetching data so that we can use the same params to select the data from data state
    const fetchDataParams = useRef<{
        readonly aggregation?: ExtTimeSeriesAggregation | undefined;
        readonly start: number;
        readonly end?: number | undefined;
        readonly limit?: number | undefined;
        readonly valueNames: readonly string[];
        readonly tagNames: readonly string[];
    } | undefined>();


    const [globalTimeSeriesInfo, setGlobalTimeSeriesInfo] = useState<GlobalTimeSeriesInfo<(NumericMetricInfo | TagMetricInfo)[]>>();

    /**
     * Wait for collectionInfo and collectionRange data to be loaded and create the globalTimeSeriesInfo
     */
    useEffect(() => {
        if (collectionHomeId && !globalTimeSeriesInfo) {

            const tsCollectionInfo = selectTimeSeriesCollectionInfo(
                appDataState, collectionHomeId, props.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID, props.collectionId,
            );
            const tsCollectionDataRange = selectTimeSeriesCollectionRange(
                appDataState, collectionHomeId, props.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID, props.collectionId,
            );

            // If we have tsCollectionDataRange then we can continue or if we don't BUT if it is because timeRangeCallFailed then also continue
            if (tsCollectionInfo && (tsCollectionDataRange || timeRangeCallFailed === true) && project) {

                // Create a NumericMetricInfo for each valueName in the collection. If NumericMetricInfos is provided by the container,
                // use that list of numeric metrics info. If not, create one
                const numericMetrics = sortMetrics(props.numericMetrics
                    ? props.numericMetrics.filter((metric: NumericMetricInfo) => {
                        return tsCollectionInfo.valueNames.includes(metric.name);
                    })
                    : tsCollectionInfo.valueNames.map((valueName: string) => {
                        // If numeric metric info is not provided by the container, create one. Since "valueName" is the only info we
                        // have, the numeric metric info object will only have this prop.
                        return {
                            name: valueName,
                        };
                    }));

                // Create a TagMetricInfo for each tagName in the collection. If TagMetricInfo is provided by the container,
                // use that list of tag metrics info. If not, create one
                const tagMetrics = tsCollectionInfo.tagNames ? sortMetrics(props.tagMetrics
                    ? props.tagMetrics.filter((metric: TagMetricInfo) => {
                        return tsCollectionInfo.tagNames?.includes(metric.name);
                    })
                    : tsCollectionInfo.tagNames.map((tagName: string) => {
                        // If tag metric info is not provided by the container, create one. Since "tagName" is the only info we
                        // have, the tag metric info object will only have this prop.
                        return {
                            name: tagName,
                        };
                    })) : undefined;

                // Now we have all the information we need to create a GlobalTimeSeriesInfo object
                setGlobalTimeSeriesInfo({
                    dateRange: {
                        start: getMidnightDate(moment(tsCollectionDataRange?.begin || project.creationTime).toDate()).valueOf(),
                        end  : getMidnightDate(moment(tsCollectionDataRange?.end || (new Date()).toISOString()).add(1, 'day').toDate()).valueOf(),
                    },
                    actualDateRange: {
                        start: moment(tsCollectionDataRange?.begin || project.creationTime).valueOf(),
                        end  : moment(tsCollectionDataRange?.end || (new Date()).toISOString()).valueOf(),
                    },
                    numericMetrics,
                    tagMetrics,
                });
            }
        }
    }, [appDataState, collectionHomeId, globalTimeSeriesInfo, project, props.collectionId, props.numericMetrics, props.tagMetrics,
        props.tsdbSmartModuleId, timeRangeCallFailed]);


    // We will keep the last requested data in cache. Before we request for new data, we will check this cache first to see if we have data for
    // the request params. If yes, we return data from the cache. If not, we will fetch new data.
    const [cachedMetricsData, setCachedMetricsData] = useState<CachedMetricData>({
        start: undefined, end: undefined, limit: undefined, numericMetrics: {}, tagMetrics: {},
    });

    // Whenever cachedTimeSeriesData is changed, we will localize it right away so that it can be displayed in the chart/table.
    // This data set can be large so we don't want to do this on every render
    const multiNumericMetricDataPoints = useMergeCachedMetricDataToMultiMetricDataPoints(cachedMetricsData);

    // Whenever cachedTimeSeriesData is changed, we will localize it right away so that it can be displayed in the chart/table.
    // This data set can be large so we don't want to do this on every render
    const multiTagMetricDataPoints = useMergeCachedMetricDataToMultiMetricDataPoints(cachedMetricsData);


    // Transform the multiTagMetricDataPoints.tagMetric value to user friendly value IF we have tag metric value mapping
    // If not, this will return the same Object as the multiTagMetricDataPoints.tagMetric
    const transformedMultiTagMetricDataPoints = useMemo(() => {
        if (multiTagMetricDataPoints?.tagMetrics && props.tagMetrics) {

            /**
             * Build a map of tag value to displayName so that we don't need to lookup the robotDefinition.tagMetrics every time. This will create
             * a structure like this
             *      {
             *          'state': {
             *              '001': 'normal',
             *              '002': 'warn',
             *              '003': 'critical,
             *          },
             *          'version': {
             *              '1.0.0.0': 'Cupcake',
             *              '2.0.0.0': 'Gingerbread',
             *              '3.0.0.0': 'Honeycomb',
             *          }
             *      }
             */
            const tagValueDisplayNameMap: {
                [tagName: string]: {
                    [tagValue: string]: string
                }
            } = props.tagMetrics
                ? props.tagMetrics.reduce((
                    result: {
                        [tagName: string]: {
                            [tagValue: string]: string
                        },
                    },
                    tagMetric: TagMetricInfo,
                ) => {
                    return {
                        ...result,
                        [tagMetric.name]: tagMetric.enumDefinitions
                            ? tagMetric.enumDefinitions.reduce((
                                valueMap: {
                                    [tagValue: string]: string
                                },
                                mappedValue: TagMetricEnum,
                            ) => {
                                return {
                                    ...valueMap,
                                    [mappedValue.value]: mappedValue.name,
                                };
                            }, {})
                            : {},
                    };
                }, {})
                : {};


            return multiTagMetricDataPoints.tagMetrics.map((msDataPoint: MultiSeriesDataPoint<string>): MultiSeriesDataPoint<string> => {
                return {
                    ...msDataPoint,
                    values: Object.keys(msDataPoint.values).reduce((formattedValues: {
                        [tagName: string]: string | null
                    }, tagName: string) => {
                        const tagValue = msDataPoint.values[tagName];
                        if (tagValue == null) {
                            return {
                                ...formattedValues,
                                [tagName]: null,
                            };
                        }
                        return {
                            ...formattedValues,
                            [tagName]: tagValueDisplayNameMap[tagName] && tagValueDisplayNameMap[tagName][tagValue]
                                ? tagValueDisplayNameMap[tagName][tagValue]
                                : tagValue,
                        };
                    }, {}),
                };
            });
        }

        return multiTagMetricDataPoints?.tagMetrics;
    }, [multiTagMetricDataPoints, props.tagMetrics]);


    /**
     * This useEffect is used for loading device data only if homeId is not provided.
     */
    useEffect(() => {
        if (!props.homeId && props.deviceId && !device) {
            dataDispatch(fetchProjectDeviceById(props.projectId, props.deviceId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [props.projectId, dataDispatch, device, props.deviceId, props.homeId]);



    /**
     * This useEffect is used for loading time series collection info and data range on component load. This will only load
     * time series collection info but not data.
     */
    useEffect(() => {
        if (collectionHomeId) {
            // dispatch a couple of actions to fetch collection info and collection range
            appDataDispatch(
                fetchTimeSeriesCollectionInfo(
                    collectionHomeId, props.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID, props.collectionId, true,
                ), true, true,
            ).catch(() => {
                // ignore error because dataDispatch already handled the error
            });

            appDataDispatch(fetchTimeSeriesCollectionRange(
                collectionHomeId, props.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID, props.collectionId,
            ), true, true).then(() => {
                // timeRange call success
                setTimeRangeCallFailed(false);
            }).catch(() => {
                // timeRange call failed
                setTimeRangeCallFailed(true);
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [collectionHomeId, props.tsdbSmartModuleId, props.collectionId, appDataDispatch]);



    /**
     * This useEffect is used for waiting for collection data to be fetched and then cache them
     */
    useEffect(() => {
        if (collectionHomeId && fetchDataParams.current && succeedFetchDataResults) {

            // Use the fetchDataParams we saved earlier to fetch the collection data
            const lastFetchDataParams = fetchDataParams.current;

            const allCollectionData = [
                lastFetchDataParams.aggregation === undefined && lastFetchDataParams.limit
                    ? selectTimeSeriesCollectionRawData(
                        appDataState,
                        collectionHomeId,
                        props.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID,
                        props.collectionId,
                        moment(lastFetchDataParams.start).toISOString(),
                        lastFetchDataParams.limit,
                        lastFetchDataParams.valueNames,
                        lastFetchDataParams.tagNames,
                    )
                    : succeedFetchDataResults.map((fetchResult) => {
                        return selectTimeSeriesCollectionData(
                            appDataState, collectionHomeId,
                            props.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID,
                            props.collectionId,
                            (new Date(lastFetchDataParams.start)).toISOString(),
                            (new Date(lastFetchDataParams.end as number)).toISOString(),
                            lastFetchDataParams.valueNames,
                            fetchResult.aggregation,
                        );
                    }),
            ].flat();

            // Filter out undefined data from failed fetch all
            const collectionsData = allCollectionData.filter((data) => {
                return data !== undefined;
            }) as (TimeSeriesCollectionRawData | TimeSeriesCollectionData)[];


            if (collectionsData && collectionsData.length > 0) {
                // We have fetch and select the data successfully, we can now clear the fetchDataParams
                fetchDataParams.current = undefined;
                setSucceedFetchDataResults(undefined);

                // Convert collection's data format from backend to a map of metricName => DataPoints array
                const reformattedNewData = collectionsData.reduce((result, collectionData): {
                    readonly numericMetrics: {
                        readonly [metricName: string]: DataPoint<number>[];
                    };
                    readonly tagMetrics: {
                        readonly [metricName: string]: DataPoint<string>[];
                    };
                } => {
                    /**
                     * Reformatted data will be in this format
                     *  {
                     *      numericMetrics: {
                     *          temperature: [...], pressure: [...]
                     *      },
                     *      tagMetrics: {
                     *          errors: [...],
                     *      }
                     *  }
                     * Because sometime we fetch multiple collection, for AGV_MIN_MAX aggregation, we have to merge them into 1 object. But because
                     * avg, min, and max uses the same name, we have to give min and max metrics different name. We will add _min and _max to the
                     * metrics' name like this
                     *  {
                     *      numericMetrics: {
                     *          temperature: [...], temperature_min: [...], temperature_max: [...],
                     *          pressure: [...], pressure_min: [...], pressure_max: [...]
                     *      },
                     *      // tagMetrics doesn't have min, max so we don't need to worry about tag metrics
                     *      tagMetrics: {
                     *          errors: [...],
                     *      }
                     *  }
                     */
                    const newCollectionReformattedData = convertCollectionDataToMetricsDataPoints(
                        collectionData,
                        lastFetchDataParams.valueNames,
                        lastFetchDataParams.aggregation === undefined ? lastFetchDataParams.tagNames : undefined,
                    );

                    // Check if we need to append some string to the metric's name
                    const metricNamePostfix = (() => {
                        if (lastFetchDataParams.aggregation === ExtTimeSeriesAggregation.AVG_MIN_MAX) {
                            // append _min or _max depending on the collectionData's aggregation
                            if ((collectionData as TimeSeriesCollectionData).aggregation === TimeSeriesAggregation.MIN) {
                                return MIN_DATA_FIELD_POSTFIX;
                            } if ((collectionData as TimeSeriesCollectionData).aggregation === TimeSeriesAggregation.MAX) {
                                return MAX_DATA_FIELD_POSTFIX;
                            }
                        }
                        // Don't append anything to the metric name
                        return '';
                    })();

                    // Merge the new collection's reformatted data into the result but make sure use different name for metrics data
                    // for min and max aggregation
                    const mergedNumericMetricsData = result.numericMetrics;

                    Object.keys(newCollectionReformattedData.numericMetrics).forEach((metricName) => {
                        if (metricName !== DUMMY_VALUE_NAME) {
                            mergedNumericMetricsData[`${metricName}${metricNamePostfix}`] = newCollectionReformattedData.numericMetrics[metricName];
                        }
                    });

                    return {
                        ...result,
                        numericMetrics: mergedNumericMetricsData,
                        tagMetrics    : {
                            ...result.tagMetrics,
                            ...newCollectionReformattedData.tagMetrics,
                        },
                    };
                }, { numericMetrics: {}, tagMetrics: {} });



                const fixedTagMetricData = lastFetchDataParams.aggregation === undefined
                    ? reformattedNewData.tagMetrics
                    : lastFetchDataParams.tagNames.reduce((
                        result: {
                            readonly [metricName: string]: DataPoint<string>[];
                        },
                        name: string,
                    ) => {
                        // If we fetched aggregated data then that means tagMetrics will be empty. We have to add metrics names to the objects but
                        // can leave the array of data empty e.g.
                        //  {
                        //      error: [],
                        //      state: []
                        //  }
                        return {
                            ...result,
                            [name]: [],
                        };
                    }, {});


                // Find the SMALLEST data resolution from all the collection and use that as the data resolution for all the data.
                const dataResolution = lastFetchDataParams.aggregation !== undefined ? collectionsData.reduce((
                    result: TimeSeriesResolution | undefined, collectionData,
                ): TimeSeriesResolution | undefined => {
                    const res = (collectionData as TimeSeriesCollectionData).resolution;
                    if (!result) {
                        // If this is the FIRST data resolution we found, use this resolution
                        return res as TimeSeriesResolution;
                    }

                    // If we already found a data resolution AND if the new collectionData also has a resolution, compare them and use the smaller one
                    if (result && res) {
                        if (resolutionToMilliseconds(result as string) > resolutionToMilliseconds(res)) {
                            return res as TimeSeriesResolution;
                        }
                    }

                    return result;
                }, undefined) : undefined;


                // Now cache the newData
                setCachedMetricsData(produce(cachedMetricsData, (cache: Draft<typeof cachedMetricsData>) => {
                    if (lastFetchDataParams.aggregation !== undefined) {
                        // If aggregated data, we can assume the
                        cache.dataResolution = dataResolution;
                        cache.dataResolutionValue = dataResolution ? resolutionToMilliseconds(dataResolution as string) : undefined;
                    }

                    if (
                        lastFetchDataParams.aggregation !== cache.aggregation
                        || lastFetchDataParams.start !== cache.start
                        || lastFetchDataParams.end !== cache.end
                        || lastFetchDataParams.limit !== cache.limit
                    ) {
                        // If date changed or limit changed, replace all the cached data with new data since to old data no longer valid
                        cache.aggregation = lastFetchDataParams.aggregation;
                        cache.start = lastFetchDataParams.start;
                        cache.end = lastFetchDataParams.end;
                        cache.limit = lastFetchDataParams.limit;
                        cache.numericMetrics = reformattedNewData.numericMetrics;
                        cache.tagMetrics = fixedTagMetricData;
                    } else {
                        // If date has not changed, continue using the previous old data and add new data to the cached data by
                        // copying the current data from cachedTimeSeriesData and override some of the data with new data
                        cache.numericMetrics = {
                            ...cache.numericMetrics,
                            ...reformattedNewData.numericMetrics,
                        };
                        cache.tagMetrics = {
                            ...cache.tagMetrics,
                            ...fixedTagMetricData,
                        };
                    }
                }));

            }
        }
    }, [collectionHomeId, props.collectionId, dataState, cachedMetricsData, props.tsdbSmartModuleId, appDataState, succeedFetchDataResults]);



    /**
     * Handle fetch time series collection data when TimeSeriesDataDisplayContainer needs to fetch more data
     */
    const onFetchTimeSeriesDataHandler = useCallback(async (
        selectedNumericMetrics: readonly NumericMetricInfo[], selectedTagMetrics: readonly TagMetricInfo[],
        aggregation: ExtTimeSeriesAggregation | undefined,
        start: number,
        end: number | undefined,        // undefined if aggregation type IS undefined
        limit: number | undefined,      // undefined if aggregation type IS NOT undefined
    ) => {
        // first, check to see which time series we need to load. We don't need to load time series for all selected metrics
        // because if the user just added a metric to the list of selected metric, we only need to load data for that metric
        // but not the other metrics which we already have data for
        const filteredNumericMetrics = selectedNumericMetrics.filter((numericMetric: NumericMetricInfo) => {
            // Return true if we have not cache ANY time series data or we don't have cached data for this specific metric
            // or we have cached data for this metric but the cached start/end date or limit is different.
            return !cachedMetricsData || !cachedMetricsData.numericMetrics
                    || !cachedMetricsData.numericMetrics[numericMetric.name]
                    || cachedMetricsData.aggregation !== aggregation
                    || (cachedMetricsData.start === undefined || cachedMetricsData.start.valueOf() !== start.valueOf())
                    || (limit && (cachedMetricsData.limit === undefined || cachedMetricsData.limit.valueOf() !== limit.valueOf()))
                    || (end && (cachedMetricsData.end === undefined || cachedMetricsData.end.valueOf() !== end.valueOf()));
        });

        // first, check to see which time series we need to load. We don't need to load time series for all selected metrics
        // because if the user just added a metric to the list of selected metric, we only need to load data for that metric
        // but not the other metrics which we already have data for
        const filteredTagMetrics = selectedTagMetrics.filter((tagMetric: TagMetricInfo) => {
            // Return true if we have not cache ANY time series data or we don't have cached data for this specific metric
            // or we have cached data for this metric but the cached start or end date is different.
            return !cachedMetricsData || !cachedMetricsData.tagMetrics
                    || !cachedMetricsData.tagMetrics[tagMetric.name]
                    || cachedMetricsData.aggregation !== aggregation
                    || (cachedMetricsData.start === undefined || cachedMetricsData.start.valueOf() !== start.valueOf())
                    || (limit !== undefined && (cachedMetricsData.limit === undefined || cachedMetricsData.limit.valueOf() !== limit.valueOf()))
                    || (end !== undefined && (cachedMetricsData.end === undefined || cachedMetricsData.end.valueOf() !== end.valueOf()));
        });


        // now request data for the list of filtered metrics
        if ((filteredNumericMetrics.length > 0 || filteredTagMetrics.length > 0) && collectionHomeId && limit !== undefined) {

            // We need to do this check in many places so create a variable for this for convenience
            const isAvgMinMaxAggregation = aggregation === ExtTimeSeriesAggregation.AVG_MIN_MAX;

            // get the metrics' valueNames. To fetch collection data, we need at least 1 valueName so if there is no numericMetrics, we
            // will create an Array of valueNames with just 1 valueName call 'dummy_value_name'
            const valueNames = filteredNumericMetrics.length > 0 ? filteredNumericMetrics.map((numericMetric: NumericMetricInfo) => {
                return numericMetric.name;
            }) : [DUMMY_VALUE_NAME];

            // get the tag metrics' tagNames
            const tagNames = filteredTagMetrics.map((tagMetric: TagMetricInfo) => {
                return tagMetric.name;
            });


            // If isMinMaxAvgAggregation, create 3 actions to fetch min/max/avg aggregated data.
            // Else fetch data for 1 aggregation
            const aggregationsToFetch = isAvgMinMaxAggregation ? AVG_MIN_MAX_AGGREGATIONS : [(aggregation as unknown) as TimeSeriesAggregation];


            // Because we need to make multiple API call to fetch data, we will need to compile a list of fetch actions and then
            // use Promise.all to execute them and wait for all of them to finish because we can try to use the data
            const fetchActions: Promise<void>[] = [];

            if (aggregation === undefined && limit) {
                // Dispatch an action to fetch RAW collection data for the list of valueNames and tagNames
                fetchActions.push(appDataDispatch(fetchTimeSeriesCollectionRawData(
                    collectionHomeId, props.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID, props.collectionId,
                    moment(start).toISOString(),
                    limit,
                    valueNames,
                    tagNames,
                ), true, true));
            } else if (end) {
                // If isMinMaxAvgAggregation, create 3 actions to fetch min/max/avg aggregated data, AVG_MIN_MAX_AGGREGATIONS.
                // Else fetch data for 1 aggregation
                for (let i = 0; i < aggregationsToFetch.length; i += 1) {
                    fetchActions.push(
                        appDataDispatch(fetchTimeSeriesCollectionData(
                            collectionHomeId,
                            props.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID,
                            props.collectionId,
                            (new Date(start)).toISOString(),
                            (new Date(end)).toISOString(),
                            valueNames,
                            aggregationsToFetch[i],
                        ), true, true),
                    );
                }
            }


            // After the data is loaded, we will need to select it from the data state. To select it from the data state, we will need to specify
            // the params we use when we fetch the data. Therefore, we need to save these params so that we can use them to select data later.
            fetchDataParams.current = {
                aggregation, start, end, limit, valueNames, tagNames,
            };


            // Wait for all the fetch data action to be done and compile a list of fetch data call that was fetched successfully.
            const succeedFetchResults = await Promise.allSettled(fetchActions.flat())
                .then((allResults) => {

                    // Loop through the list of 'allResults' and create a FetchResult object for each succeed fetch calls. If a call failed
                    // return undefined. By the end of this loop, 'allFetchResults', should contain an array of 'FetchResult' objects and
                    // maybe some 'undefined'
                    const allFetchResults = allResults.map((
                        singleResult, resultIndex: number,
                    ): FetchDataResult | undefined => {
                        if (singleResult.status === 'fulfilled') {
                            // if isMinMaxAvgAggregation then the aggregation will depend on the resultIndex. Because we make API call
                            // in this order AVG, MIN, MAX then the results will also be in this order
                            // result at index 0, 3, 6, etc... will be for the API calls that fetch AGV data
                            // result at index 1, 4, 7, etc... will be for the API calls that fetch MIN data
                            // result at index 2, 5, 8, etc... will be for the API calls that fetch MAX data
                            return {
                                aggregation: aggregationsToFetch[resultIndex % aggregationsToFetch.length],
                            };
                        }
                        return undefined;
                    });

                    // Filter out 'undefined' results because those are unsuccess fetch alls
                    return allFetchResults.reduce((result: FetchDataResult[], singleFetchResult: FetchDataResult | undefined) => {
                        if (singleFetchResult) {
                            return [...result, singleFetchResult];
                        }
                        return result;
                    }, []);
                });

            setSucceedFetchDataResults(succeedFetchResults);
        }

    }, [appDataDispatch, cachedMetricsData, collectionHomeId, props.collectionId, props.tsdbSmartModuleId]);



    /**
     * On Download Data clicked handler. Show the Download Data Request panel and pass it the collections the user is trying to download
     * and set the default selection data range to 1 Month
     */
    const downloadDataHandler = useCallback(() => {
        if (globalTimeSeriesInfo && collectionHomeId) {
            const options: DownloadTSDataCompOptions = {
                location : SlideOutPanelLocation.RIGHT,
                compType : SlideOutPanelCompType.DOWNLOAD_TS_DATA_REQUEST,
                compProps: {
                    data: {
                        projectId        : props.projectId,
                        homeId           : collectionHomeId,
                        tsdbSmartModuleId: props.tsdbSmartModuleId || DEFAULT_TSDB_SMART_MODULE_ID,
                        dataInfo         : [{
                            type    : TimeSeriesType.COLLECTION,
                            id      : props.collectionId,
                            name    : props.collectionDisplayName,
                            selected: true,
                        }],
                        startDate: Math.max(globalTimeSeriesInfo.actualDateRange.start, globalTimeSeriesInfo.actualDateRange.end - Time.MONTH_IN_MS),
                        endDate  : globalTimeSeriesInfo.actualDateRange.end,
                        minDate  : globalTimeSeriesInfo.actualDateRange.start,
                        maxDate  : globalTimeSeriesInfo.actualDateRange.end,
                    },
                },
            };
            uiDispatch(showSlideOutPanel(options));
        }
    }, [collectionHomeId, globalTimeSeriesInfo, props.collectionDisplayName, props.collectionId, props.projectId, props.tsdbSmartModuleId,
        uiDispatch]);


    /**
     * Create a Download Button which will be passed to the parent container for display
     */
    const downloadButton = useMemo(() => {
        return (
            <Button
                color="primary"
                variant="contained"
                startIcon={<Icon>get_app</Icon>}
                disabled={!(globalTimeSeriesInfo && collectionHomeId)}
                onClick={() => {
                    downloadDataHandler();
                }}
            >{capitalizeAll(trans('label.download_time_series_data'))}
            </Button>
        );
    }, [collectionHomeId, downloadDataHandler, globalTimeSeriesInfo, trans]);


    /**
     * on component init, call the parent's setExternalComp handler function and pass it the download button to display
     */
    useEffect(() => {
        props.setPageMainAction(downloadButton);
    }, [downloadButton, props, props.setPageMainAction]);



    const isFetchingData = Boolean(
        !project
        || !collectionHomeId
        || !globalTimeSeriesInfo,
    );


    const hasNumericMetricData = globalTimeSeriesInfo && globalTimeSeriesInfo.numericMetrics && globalTimeSeriesInfo.numericMetrics.length > 0
        && multiNumericMetricDataPoints && multiNumericMetricDataPoints.numericMetrics !== undefined;

    const hasTagMetricData = globalTimeSeriesInfo && globalTimeSeriesInfo.tagMetrics && globalTimeSeriesInfo.tagMetrics.length > 0
        && multiTagMetricDataPoints && multiTagMetricDataPoints.tagMetrics !== undefined;


    return (
        <>
            {!isFetchingData && (
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        {globalTimeSeriesInfo && (hasNumericMetricData || hasTagMetricData) && (
                            <MetricsDataDisplayContainer
                                projectId={props.projectId}
                                absoluteStartDate={globalTimeSeriesInfo.actualDateRange.start}
                                absoluteEndDate={globalTimeSeriesInfo.actualDateRange.end}
                                numericMetrics={globalTimeSeriesInfo.numericMetrics}
                                multiSeriesNumericDataPoints={multiNumericMetricDataPoints?.numericMetrics || []}
                                tagMetrics={globalTimeSeriesInfo.tagMetrics}
                                multiSeriesTagDataPoints={transformedMultiTagMetricDataPoints || []}
                                fetchTimeSeriesDataHandler={onFetchTimeSeriesDataHandler}
                                viewOptions={props.viewOptions}
                                viewTypeSettings={props.viewTypeSettings}
                                dataResolution={cachedMetricsData.dataResolution}
                                dataLimit={120}
                            />
                        )}

                        {!hasNumericMetricData && !hasTagMetricData && (
                            /* Show empty message if there is no time series */
                            <Box marginTop={3}>
                                <GenericMessageBox
                                    title={trans('generic.no_data.title', {
                                        term: capitalizeAll(trans('label.time_series_data', { count: 2 })),
                                    })}
                                />
                            </Box>
                        )}
                    </Grid>
                </Grid>
            )}


            {isFetchingData && (
                <CircularProgress />
            )}
        </>
    );
};
